import {useMemo, useState, useCallback} from "react";
import {encodeFunctionData, type Abi, type Address} from "viem";
import {useSendTransaction, usePublicClient, useAccount} from "wagmi";
import type {TrackedTx, AsyncTxState} from "@/lib/types";

/**
 * CRITICAL: wagmi's `useWriteContract` / `writeContractAsync` runs
 * `simulateContract` (eth_call) before sending. Against an async precompile
 * (0x0801/0x0802) — or any contract function that internally calls one — eth_call
 * returns "call to non-contract address" or a reverted simulation. So we MUST use
 * `useSendTransaction` + `encodeFunctionData` to skip simulation entirely.
 *
 * This hook wraps that pattern and drives the 9-state machine from the receipt.
 */
export function useRitualWrite() {
  const {sendTransactionAsync, isPending} = useSendTransaction();
  const publicClient = usePublicClient();
  const {address} = useAccount();
  const [tx, setTx] = useState<TrackedTx | null>(null);

  const transition = useCallback((state: AsyncTxState, patch?: Partial<TrackedTx>) => {
    setTx((prev) =>
      prev ? {...prev, state, ...patch, ...(state === "ERROR" ? {} : {})} : prev
    );
  }, []);

  const write = useCallback(
    async (params: {
      address: Address;
      abi: Abi;
      functionName: string;
      args?: unknown[];
      value?: bigint;
      gas?: bigint;
      label: string;
    }) => {
      if (!address) throw new Error("wallet not connected");
      const data = encodeFunctionData({
        abi: params.abi,
        functionName: params.functionName,
        args: params.args ?? [],
      });

      setTx({state: "CONFIRMING", label: params.label, startedAt: Date.now()});

      try {
        const hash = await sendTransactionAsync({
          to: params.address,
          data,
          value: params.value,
          gas: params.gas,
        });
        setTx({state: "SUBMITTED", label: params.label, startedAt: Date.now(), hash});
        transition("SUBMITTED", {hash});

        if (!publicClient) throw new Error("no public client");
        // waitForTransactionReceipt waits for Phase 1 (tx mined). For short-running
        // async the settled result is in the receipt's spcCalls in the SAME block.
        const receipt = await publicClient.waitForTransactionReceipt({hash});
        transition("SETTLED", {hash});
        return {hash, receipt};
      } catch (e) {
        setTx((prev) =>
          prev
            ? {...prev, state: "ERROR", error: e instanceof Error ? e.message : String(e)}
            : null
        );
        throw e;
      }
    },
    [sendTransactionAsync, publicClient, address, transition]
  );

  const reset = useCallback(() => setTx(null), []);

  return useMemo(() => ({write, reset, tx, isPending}), [write, reset, tx, isPending]);
}
